import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { useLocalStorage } from '@vueuse/core';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useBilling from '@/composables/useBilling';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import useIteratively from '@/composables/useIteratively';
import useRecordExportLimits from '@/composables/useRecordExportLimits';
import appConfig from '@/config';
import {
  FEATURE_FLAG,
  ORGANIZATION_PAYMENT_REQUIRED,
} from '@/constants/feature_flags';
import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import { buildUsageLimitAlert } from '@/constants/usage_alerts';
import {
  useBillingStore,
  useFeatureFlagStore,
  useFeedsStore,
  useSourcesStore,
} from '@/stores';
import type { AlertType, AppAlert } from '@/types/alerts';
import { computeCreditLimitStatus } from '@/utils/creditLimitStatus';

import { usePerformanceDashboardFilters } from './usePerformanceDashboardFilters';

export default function () {
  const billingStore = useBillingStore();
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const { iteratively } = useIteratively();
  const { hasFeatureFlag } = useFeatureFlagStore();
  const {
    isSubscriptionPastDue,
    chargifyData,
    isOnFreeTrial,
    creditLimit,
    creditsUsed,
  } = storeToRefs(billingStore);

  const { isAdminPanelUser, isAdmin, currentOrg, currentUser } = useAuth();
  const { feedNamesReadyForPreview } = storeToRefs(feedsStore);
  const route = useRoute();
  const router = useRouter();
  const { filters } = usePerformanceDashboardFilters();

  /* Admin Alert: Those who are oauth'ed into production as an admin */
  const adminAlert = computed(() =>
    isAdminPanelUser.value && appConfig.env === 'prod'
      ? ({
          message: `You are logged into production (${currentOrg.value.name}) as an admin!`,
          type: 'admin',
        } satisfies AppAlert)
      : null,
  );

  /* Feed Alert */
  const feedAlert = computed(() => {
    if (feedNamesReadyForPreview.value.length > 0) {
      const feeds = feedNamesReadyForPreview.value;
      const feedList =
        feeds.length === 1
          ? feeds[0]
          : `${feeds.slice(0, -1).join(', ')} and ${feeds[feeds.length - 1]}`;

      const message =
        feeds.length === 1
          ? `Your ${feedList} data source is connected in preview mode. It's not yet fully synced, but you can start segmenting it into populations.`
          : `Your ${feedList} data sources are connected in preview mode. They are not yet fully synced, but you can start segmenting them into populations.`;

      return {
        icon: ['fak', 'loading'],
        message,
        type: 'feed-in-preview-mode',
      } satisfies AppAlert;
    }
    return null;
  });

  const performanceDashboardAlert = computed(() => {
    if (
      route.path !== '/measure/ecosystem' ||
      filters.value.timePeriod === 'thisQuarter'
    )
      return null;

    const message =
      'We started tracking engagement data on January 1, 2025. Insights from before that date are based on estimates.';

    return {
      message,
      type: 'neutral',
    } satisfies AppAlert;
  });

  /* Billing Alert (Overdue Alert) */
  const billingAlert = computed(() => {
    if (isSubscriptionPastDue.value) {
      const billingDate = DateTime.fromISO(
        chargifyData.value?.open_invoice_issue_date ?? '',
      );
      const cancellationDate = billingDate
        .plus({ days: 30 })
        .toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' });
      return {
        type: 'past_due',
        buttonText: 'Update billing',
        message: `Your payment failed, we will try the card again until ${cancellationDate} and then suspend your account`,
        path: 'edit-payment',
      } satisfies AppAlert;
    }
    return null;
  });

  const { daysLeftText, isTrialOver } = useConnectorTrialEndDate();

  /* You are on free trial! */
  const freeTrialAlert = computed(() => {
    if (!isOnFreeTrial.value || isTrialOver.value) return null;
    return {
      message: `You’re currently on a Connector free trial. You have ${daysLeftText.value} remaining before it ends`,
      type: 'connector_trial',
      buttonText: 'Upgrade to Connector now',
    } satisfies AppAlert;
  });

  /* Your free trial has expired */
  const freeTrialEndedAlert = computed(() => {
    if (!isTrialOver.value) return null;
    return {
      icon: ['fak', 'warning'],
      message:
        'Your free trial has ended - upgrade now to retain your Connector level access',
      type: 'trial_ended',
      buttonText: 'Upgrade',
    } satisfies AppAlert;
  });

  const isMcpMeteringUi = computed(() =>
    hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_METERING_UI),
  );
  const { exportLimitStatus, RECORD_EXPORT_ALERTS } = useRecordExportLimits();
  const recordAlertStatus = computed(() => {
    const status = exportLimitStatus.value;
    return status === 'over-90' || status === 'limit-reached' ? status : null;
  });
  const creditAlertStatus = computed(() =>
    isMcpMeteringUi.value && hasFeatureFlag('temp_mcp_credit_check')
      ? computeCreditLimitStatus(creditsUsed.value, creditLimit.value)
      : null,
  );

  const usageLimitAlert = computed(() => {
    if (!isMcpMeteringUi.value)
      return recordAlertStatus.value
        ? RECORD_EXPORT_ALERTS[recordAlertStatus.value]
        : null;
    return buildUsageLimitAlert(
      recordAlertStatus.value,
      creditAlertStatus.value,
    );
  });

  const usageLimitBillingCta = computed(() => {
    if (recordAlertStatus.value && creditAlertStatus.value) {
      return {
        cta: PLAN_AND_BILLING_CTAS.USAGE_LIMIT,
        talkToSalesReason: 'Record Export and credit limits',
      };
    }
    if (creditAlertStatus.value) {
      return {
        cta: PLAN_AND_BILLING_CTAS.CREDIT_LIMIT,
        talkToSalesReason: 'Credit limit',
      };
    }
    return {
      cta: PLAN_AND_BILLING_CTAS.RECORD_EXPORT_LIMIT,
      talkToSalesReason: 'Record Export limit',
    };
  });

  const usageAlertAndOrgId = computed(() =>
    isMcpMeteringUi.value
      ? `usage-limit__${recordAlertStatus.value ?? 'none'}__${
          creditAlertStatus.value ?? 'none'
        }__${currentOrg.value.id}`
      : `${exportLimitStatus.value}__${currentOrg.value.id}`,
  );
  const dismissedLimitAlerts = ref<string[]>([]);

  const showUsageLimitAlert = computed(() => {
    if (!usageLimitAlert.value) return false;
    return !dismissedLimitAlerts.value.includes(usageAlertAndOrgId.value);
  });

  watch(usageAlertAndOrgId, (newVal) => {
    const usageLimitAlertDismissal = useLocalStorage<string | null>(
      newVal,
      null,
    );
    if (usageLimitAlertDismissal.value === 'dismissed') {
      dismissedLimitAlerts.value.push(usageAlertAndOrgId.value);
    }
  });

  /* Payment Required Alert */
  const paymentAlert = computed(() => {
    if (!hasFeatureFlag(ORGANIZATION_PAYMENT_REQUIRED)) return null;
    return {
      icon: ['fak', 'warning'],
      message:
        'Your organization requires payment to continue using Crossbeam. Please contact your administrator or billing team to resolve this issue.',
      type: 'danger',
    } satisfies AppAlert;
  });

  const accountOwnerFieldSyncFieldName = computed(() => {
    const unsyncedActiveField = feedsStore.crmFeeds
      .flatMap((crmFeed) => sourcesStore.getSourcesByFeedId(crmFeed.id))
      .filter((source) => source.mdm_type === MDM_TYPES.USER)
      .flatMap((source) => source.fields)
      .find(
        (field) =>
          field.mdm_property === MDM_PROPERTIES[MDM_TYPES.USER].IS_ACTIVE &&
          !field.is_visible &&
          !field.is_filterable,
      );
    return unsyncedActiveField?.display_name ?? null;
  });

  const isAccountOwnerFieldSyncDismissed = useLocalStorage(
    () =>
      `account-owner-field-sync-banner__${currentUser.value.id}__${currentOrg.value.id}`,
    false,
  );

  const accountOwnerFieldSyncAlert = computed(() => {
    if (!hasFeatureFlag(FEATURE_FLAG.TEMP_ACCOUNT_OWNER_FIELD_SYNC_BANNER))
      return null;
    if (!isAdmin.value) return null;
    if (isAccountOwnerFieldSyncDismissed.value) return null;
    const fieldName = accountOwnerFieldSyncFieldName.value;
    if (!fieldName) return null;
    return {
      icon: ['fak', 'info'],
      message: `Starting September 30, we’ll sync the field ${fieldName} from your CRM to keep Deal Navigator account ownership accurate. Don’t want this?`,
      type: 'info',
      buttonText: 'Opt out',
      dismissable: true,
    } satisfies AppAlert;
  });

  /* Handlers for events from alerts */
  function onDismissAlert(type?: AlertType) {
    if (type === 'info') {
      isAccountOwnerFieldSyncDismissed.value = true;
    }
    const usageAlertTypes: AlertType[] = [
      'over-90',
      'limit-reached',
      'usage-limit-warning',
      'usage-limit-danger',
    ];
    if (type && usageAlertTypes.includes(type)) {
      const usageAlertStorageValue = useLocalStorage(
        usageAlertAndOrgId.value,
        'dismissed',
      );
      usageAlertStorageValue.value = 'dismissed';
      dismissedLimitAlerts.value.push(usageAlertAndOrgId.value);
    }
  }

  const { handleBillingInteraction } = useBilling();
  async function onAlertClicked({
    type,
    alert,
  }: {
    type?: AlertType;
    alert?: Pick<AppAlert, 'path'> | null;
  }) {
    if (type === 'info') {
      iteratively.optOutIsActive({
        event_site: EVENT_SITES.IS_ACTIVE_SYNC_BANNER,
      });
      isAccountOwnerFieldSyncDismissed.value = true;
    }
    const recordExportAlertTypes: AlertType[] = ['over-90', 'limit-reached'];
    if (type && recordExportAlertTypes.includes(type)) {
      handleBillingInteraction({
        cta: PLAN_AND_BILLING_CTAS.RECORD_EXPORT_LIMIT,
        talkToSalesReason: 'Record Export limit',
        event_site: EVENT_SITES.BILLING_ALERT_BANNER,
      });
    }
    const usageLimitAlertTypes: AlertType[] = [
      'usage-limit-warning',
      'usage-limit-danger',
    ];
    if (type && usageLimitAlertTypes.includes(type)) {
      handleBillingInteraction({
        ...usageLimitBillingCta.value,
        event_site: EVENT_SITES.BILLING_ALERT_BANNER,
      });
    }
    if (alert?.path)
      await router.push({
        name: alert.path,
        query: { cancel: route.name as string },
      });
    const trialAlertTypes: AlertType[] = ['connector_trial', 'trial_ended'];
    if (type && trialAlertTypes.includes(type)) {
      handleBillingInteraction({
        cta: PLAN_AND_BILLING_CTAS.FREE_TRIAL_BANNER,
        event_site: EVENT_SITES.FREE_TRIAL_BANNER,
      });
    }
  }

  const allAlerts = computed(() => {
    const alerts: AppAlert[] = [];
    if (accountOwnerFieldSyncAlert.value)
      alerts.push(accountOwnerFieldSyncAlert.value);
    if (paymentAlert.value) alerts.push(paymentAlert.value);
    if (billingAlert.value) alerts.push(billingAlert.value);
    if (adminAlert.value) alerts.push(adminAlert.value);
    if (showUsageLimitAlert.value && usageLimitAlert.value)
      alerts.push(usageLimitAlert.value);
    if (freeTrialAlert.value) alerts.push(freeTrialAlert.value);
    if (freeTrialEndedAlert.value) alerts.push(freeTrialEndedAlert.value);
    if (feedAlert.value) alerts.push(feedAlert.value);
    if (performanceDashboardAlert.value)
      alerts.push(performanceDashboardAlert.value);
    return alerts;
  });

  const numAlerts = computed(() => allAlerts.value.length);

  return {
    adminAlert,
    accountOwnerFieldSyncAlert,
    billingAlert,
    freeTrialAlert,
    freeTrialEndedAlert,
    showUsageLimitAlert,
    onDismissAlert,
    onAlertClicked,
    allAlerts,
    numAlerts,
    performanceDashboardAlert,
  };
}
